import React, { useMemo, useState } from 'react'
import { Rnd } from 'react-rnd'

export type DisplayRegion = {
  id: string
  left: number
  top: number
  width: number
  height: number
  // False when this region's underlying annotation hasn't been verified yet —
  // rendered in the warning color so it stands out from verified annotations.
  validated?: boolean
}

type DraftRect = {
  startX: number
  startY: number
  endX: number
  endY: number
}

type Props = {
  enabled: boolean
  readOnly?: boolean
  width: number
  height: number
  regions: DisplayRegion[]
  onRegionsChange: (regions: DisplayRegion[]) => void
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const normalizeDraft = (draft: DraftRect) => {
  const left = Math.min(draft.startX, draft.endX)
  const top = Math.min(draft.startY, draft.endY)
  const right = Math.max(draft.startX, draft.endX)
  const bottom = Math.max(draft.startY, draft.endY)
  return {
    left,
    top,
    width: right - left,
    height: bottom - top,
  }
}

const RegionOverlay: React.FC<Props> = ({
  enabled,
  readOnly = false,
  width,
  height,
  regions,
  onRegionsChange,
}) => {
  const [draft, setDraft] = useState<DraftRect | null>(null)
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null)

  const safeRegions = useMemo(
    () =>
      regions.map((region) => ({
        ...region,
        left: clamp(region.left, 0, Math.max(0, width - 1)),
        top: clamp(region.top, 0, Math.max(0, height - 1)),
        width: clamp(region.width, 1, width),
        height: clamp(region.height, 1, height),
      })),
    [height, regions, width],
  )

  const updateRegion = (regionId: string, patch: Partial<DisplayRegion>) => {
    onRegionsChange(
      safeRegions.map((region) =>
        region.id === regionId
          ? {
              ...region,
              ...patch,
            }
          : region,
      ),
    )
  }

  const removeRegion = (regionId: string) => {
    onRegionsChange(safeRegions.filter((region) => region.id !== regionId))
    setSelectedRegionId((prev) => (prev === regionId ? null : prev))
  }

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!enabled || readOnly) return
    if (event.target !== event.currentTarget) return
    const rect = event.currentTarget.getBoundingClientRect()
    const x = clamp(event.clientX - rect.left, 0, width)
    const y = clamp(event.clientY - rect.top, 0, height)
    setDraft({ startX: x, startY: y, endX: x, endY: y })
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draft) return
    const rect = event.currentTarget.getBoundingClientRect()
    const x = clamp(event.clientX - rect.left, 0, width)
    const y = clamp(event.clientY - rect.top, 0, height)
    setDraft((prev) => (prev ? { ...prev, endX: x, endY: y } : prev))
  }

  const commitDraft = () => {
    if (!draft) return
    const normalized = normalizeDraft(draft)
    setDraft(null)
    if (normalized.width < 2 || normalized.height < 2) return

    const nextRegion: DisplayRegion = {
      id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      ...normalized,
    }
    onRegionsChange([...safeRegions, nextRegion])
  }

  if (!enabled || width <= 0 || height <= 0) return null

  const draftRect = draft ? normalizeDraft(draft) : null

  return (
    <div
      tabIndex={0}
      style={{
        position: 'absolute',
        inset: 0,
        width: `${width}px`,
        height: `${height}px`,
        cursor: readOnly ? 'default' : 'crosshair',
        outline: 'none',
      }}
      onKeyDown={(event) => {
        if (readOnly) return
        if ((event.key === 'Delete' || event.key === 'Backspace') && selectedRegionId) {
          event.preventDefault()
          removeRegion(selectedRegionId)
        }
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={commitDraft}
      onPointerLeave={commitDraft}
    >
      {safeRegions.map((region, index) => (
        <Rnd
          key={region.id}
          bounds="parent"
          cancel=".region-delete-btn"
          disableDragging={readOnly}
          enableResizing={
            readOnly
              ? false
              : {
                  top: true,
                  right: true,
                  bottom: true,
                  left: true,
                  topLeft: true,
                  topRight: false,
                  bottomLeft: true,
                  bottomRight: true,
                }
          }
          position={{ x: region.left, y: region.top }}
          size={{ width: region.width, height: region.height }}
          onDragStop={(_, data) => {
            updateRegion(region.id, { left: data.x, top: data.y })
          }}
          onResizeStop={(_, __, ref, ___, position) => {
            updateRegion(region.id, {
              left: position.x,
              top: position.y,
              width: ref.offsetWidth,
              height: ref.offsetHeight,
            })
          }}
          style={{
            border:
              region.validated === false
                ? selectedRegionId === region.id
                  ? '2px solid #8a6d00'
                  : '2px solid #f0c040'
                : selectedRegionId === region.id
                  ? '2px solid #8f0f15'
                  : '2px solid #d7272f',
            background: region.validated === false ? 'rgba(240, 192, 64, 0.18)' : 'rgba(215, 39, 47, 0.06)',
            boxSizing: 'border-box',
          }}
          onMouseDown={(event) => {
            event.stopPropagation()
            setSelectedRegionId(region.id)
          }}
        >
          <span
            style={{
              position: 'absolute',
              right: '2px',
              bottom: '2px',
              minWidth: '16px',
              height: '16px',
              lineHeight: '16px',
              textAlign: 'center',
              fontSize: '10px',
              fontWeight: 700,
              color: region.validated === false ? '#5a4200' : '#ffffff',
              background: region.validated === false ? '#f0c040' : '#d7272f',
              borderRadius: '3px',
              padding: '0 3px',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {index + 1}
          </span>
          {!readOnly && (
            <button
              className="region-delete-btn"
              type="button"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                removeRegion(region.id)
              }}
              onMouseDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
              onPointerDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
              style={{
                position: 'absolute',
                right: '2px',
                top: '2px',
                width: '20px',
                height: '20px',
                borderRadius: '999px',
                border: '1px solid #8f0f15',
                background: '#ffffff',
                color: '#8f0f15',
                fontWeight: 700,
                cursor: 'pointer',
                padding: 0,
                zIndex: 999,
                pointerEvents: 'auto',
              }}
              aria-label="Delete region"
              title="Delete region"
            >
              x
            </button>
          )}
        </Rnd>
      ))}

      {draftRect && (
        <div
          style={{
            position: 'absolute',
            left: `${draftRect.left}px`,
            top: `${draftRect.top}px`,
            width: `${draftRect.width}px`,
            height: `${draftRect.height}px`,
            border: '2px dashed #d7272f',
            background: 'rgba(215, 39, 47, 0.08)',
            boxSizing: 'border-box',
          }}
        />
      )}
    </div>
  )
}

export default RegionOverlay
